import React, { useMemo, useState } from "react";
import { Asset, AssetRateCard, BidVersion, ProjectSetup, PurchaseOrder, RateCard, Shot } from "../types";
import { formatCurrency, getEpisodeFromShotCode } from "../utils";
import { calculateAssetValue, calculateShotValue, getCommercialSummaryMetrics, getVersionCounts } from "../lib/bidVersioning";
import { Calendar, ChevronDown, ChevronRight, FileText, History, Package, Receipt, Wallet } from "lucide-react";
import { ExportButtons } from "./ExportButtons";
import { toast } from "sonner";

interface Props {
  setup: ProjectSetup;
  rates: RateCard;
  assetRates: AssetRateCard;
  shots: Shot[];
  assets: Asset[];
  versions: BidVersion[];
  purchaseOrders: PurchaseOrder[];
  onAddPurchaseOrder: (purchaseOrder: Omit<PurchaseOrder, "id" | "remainingAmount">) => void;
}

function StatCard({ title, value }: { title: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</div>
      <div className="mt-2 text-xl font-semibold text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

export function BidSummaryDashboard({
  setup,
  rates,
  assetRates,
  shots,
  assets,
  versions,
  purchaseOrders,
  onAddPurchaseOrder,
}: Props) {
  const [expandedVersionIds, setExpandedVersionIds] = useState<Set<string>>(new Set());
  const [showPOFormForVersion, setShowPOFormForVersion] = useState<string | null>(null);
  const [poDraft, setPoDraft] = useState({
    poNumber: "",
    clientName: setup.clientName || "",
    issueDate: new Date().toISOString().split("T")[0],
    amount: "",
    linkedEpisode: "",
  });

  const summary = useMemo(
    () => getCommercialSummaryMetrics(shots, assets, setup, rates, assetRates, purchaseOrders),
    [shots, assets, setup, rates, assetRates, purchaseOrders]
  );

  const sortedVersions = useMemo(
    () => [...versions].sort((a, b) => new Date(b.exportedAt || b.createdAt).getTime() - new Date(a.exportedAt || a.createdAt).getTime()),
    [versions]
  );

  const unlinkedAwardedValue = useMemo(() => {
    const poLinkedVersionIds = new Set(purchaseOrders.map((po) => po.linkedVersionId).filter(Boolean));
    return sortedVersions
      .filter((version) => !poLinkedVersionIds.has(version.id))
      .reduce((total, version) => {
        const shotAwarded = (version.shots || []).reduce((sum, shot) => {
          if (!["Awarded", "Overage", "Invoiced"].includes(shot.status || "")) return sum;
          return sum + (shot.originalAwardedValue ?? shot.legacyPrice ?? 0);
        }, 0);
        const assetAwarded = (version.assets || []).reduce((sum, asset) => {
          if (!["Awarded", "Overage", "Invoiced"].includes(asset.status || "")) return sum;
          return sum + (asset.originalAwardedValue ?? asset.legacyPrice ?? 0);
        }, 0);
        return total + shotAwarded + assetAwarded;
      }, 0);
  }, [sortedVersions, purchaseOrders]);

  const toggleVersion = (versionId: string) => {
    setExpandedVersionIds((prev) => {
      const next = new Set(prev);
      if (next.has(versionId)) next.delete(versionId);
      else next.add(versionId);
      return next;
    });
  };

  const handleSubmitPO = (versionId: string) => {
    const amount = Number.parseFloat(poDraft.amount);
    if (!poDraft.poNumber.trim() || Number.isNaN(amount)) {
      toast.error("Add a PO number and amount first");
      return;
    }

    onAddPurchaseOrder({
      poNumber: poDraft.poNumber.trim(),
      clientName: poDraft.clientName.trim() || setup.clientName,
      issueDate: poDraft.issueDate,
      amount,
      linkedVersionId: versionId,
      linkedEpisode: poDraft.linkedEpisode.trim() || undefined,
      invoicedAmountToDate: 0,
      status: "Issued",
    });

    setPoDraft({
      poNumber: "",
      clientName: setup.clientName || "",
      issueDate: new Date().toISOString().split("T")[0],
      amount: "",
      linkedEpisode: "",
    });
    setShowPOFormForVersion(null);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <StatCard title="Total Awarded To Date" value={formatCurrency(summary.totalAwardedToDate, setup.currency)} />
        <StatCard title="Open Bidding Total" value={formatCurrency(summary.openBiddingTotal, setup.currency)} />
        <StatCard title="Total Lost" value={formatCurrency(summary.totalLost, setup.currency)} />
        <StatCard title="Total Overage Value" value={formatCurrency(summary.totalOverageValue, setup.currency)} />
        <StatCard title="PO Balance Remaining" value={formatCurrency(summary.poBalanceRemaining, setup.currency)} />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2 space-y-4">
          {sortedVersions.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              No bid versions exported yet. Exporting a quote will create the first locked snapshot.
            </div>
          ) : (
            sortedVersions.map((version) => {
              const isExpanded = expandedVersionIds.has(version.id);
              const counts = getVersionCounts(version);
              const linkedPOs = purchaseOrders.filter((po) => po.linkedVersionId === version.id);
              const episodeBreakdown =
                version.episodeBreakdown ||
                [];

              return (
                <div key={version.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <button
                    onClick={() => toggleVersion(version.id)}
                    className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60"
                  >
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{version.versionName || version.versionCode}</span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          {version.locked ? "Locked" : "Draft"}
                        </span>
                        <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
                          {version.scopeType === "subset" ? "Subset" : "Full"}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5" />
                          {new Date(version.exportedAt || version.createdAt).toLocaleString()}
                        </span>
                        <span>{counts.shotCount} shots</span>
                        <span>{counts.assetCount} assets</span>
                        <span>{counts.onsetDays} onset days</span>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-base font-semibold text-slate-900 dark:text-slate-100">
                        {formatCurrency(version.totalCost || 0, version.setup.currency)}
                      </div>
                      <div className="text-xs text-slate-400">{version.snapshotStatus || "Exported snapshot"}</div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="space-y-5 border-t border-slate-100 px-5 py-5">
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <div className="rounded-lg border border-slate-200 p-3">
                          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Shot Total</div>
                          <div className="mt-1 text-[13px] font-semibold text-slate-900">{formatCurrency(version.shotTotal || 0, version.setup.currency)}</div>
                        </div>
                        <div className="rounded-lg border border-slate-200 p-3">
                          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Asset Total</div>
                          <div className="mt-1 text-[13px] font-semibold text-slate-900">{formatCurrency(version.assetTotal || 0, version.setup.currency)}</div>
                        </div>
                        <div className="rounded-lg border border-slate-200 p-3">
                          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Onset Total</div>
                          <div className="mt-1 text-[13px] font-semibold text-slate-900">{formatCurrency(version.onsetTotal || 0, version.setup.currency)}</div>
                        </div>
                        <div className="rounded-lg border border-slate-200 p-3">
                          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Audit</div>
                          <div className="mt-1 text-xs text-slate-600">{version.auditHistory?.[0]?.details || "Created via Export Quote"}</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                        <div className="rounded-xl border border-slate-200 p-4">
                          <div className="mb-3 flex items-center gap-2">
                            <History className="h-4 w-4 text-slate-400" />
                            <h4 className="text-sm font-semibold text-slate-900">Episode Breakdown</h4>
                          </div>
                          <div className="space-y-2">
                            {episodeBreakdown.length > 0 ? (
                              episodeBreakdown.map((episode) => (
                                <div key={episode.episode} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                                  <div>
                                    <div className="text-[13px] font-medium text-slate-900">{episode.episode}</div>
                                    <div className="text-xs text-slate-500">{episode.shotCount} shots</div>
                                  </div>
                                  <div className="text-[13px] font-semibold text-slate-900">{formatCurrency(episode.totalValue, version.setup.currency)}</div>
                                </div>
                              ))
                            ) : (
                              <div className="text-sm text-slate-500">No episode-based shot breakdown captured for this version.</div>
                            )}
                          </div>
                        </div>

                        <div className="rounded-xl border border-slate-200 p-4">
                          <div className="mb-3 flex items-center gap-2">
                            <Package className="h-4 w-4 text-slate-400" />
                            <h4 className="text-sm font-semibold text-slate-900">Version Actions</h4>
                          </div>
                          <div className="grid grid-cols-1 gap-2">
                            <button
                              onClick={() => toggleVersion(version.id)}
                              className="rounded-lg border border-slate-300 px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
                            >
                              View Snapshot
                            </button>
                            <button
                              onClick={() => toast.message("Breakdown is shown below for this version.")}
                              className="rounded-lg border border-slate-300 px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
                            >
                              View Breakdown
                            </button>
                            <div className="rounded-lg border border-slate-200 p-2">
                              <ExportButtons
                                setup={version.setup}
                                rates={version.rates}
                                assetRates={version.assetRates}
                                shots={version.shots}
                                assets={version.assets}
                              />
                            </div>
                            <button
                              onClick={() => setShowPOFormForVersion(showPOFormForVersion === version.id ? null : version.id)}
                              className="rounded-lg border border-slate-300 px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
                            >
                              View Linked PO(s)
                            </button>
                            <button
                              onClick={() => toast.message(version.auditHistory?.map((entry) => `${entry.timestamp}: ${entry.action}`).join("\n") || "No extra audit entries yet.")}
                              className="rounded-lg border border-slate-300 px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
                            >
                              View Audit History
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                        <div className="rounded-xl border border-slate-200 p-4">
                          <div className="mb-3 flex items-center gap-2">
                            <FileText className="h-4 w-4 text-slate-400" />
                            <h4 className="text-sm font-semibold text-slate-900">Line Items</h4>
                          </div>
                          <div className="space-y-2 text-sm">
                            {(version.shots || []).slice(0, 6).map((shot) => (
                              <div key={shot.id} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2">
                                <div className="min-w-0">
                                  <div className="text-[13px] font-medium text-slate-900">{shot.shotCode}</div>
                                  <div className="truncate text-xs text-slate-500">
                                    {shot.status || "Bidding"} • {getEpisodeFromShotCode(shot.shotCode) || "No episode"}
                                  </div>
                                </div>
                                <div className="text-[13px] font-semibold text-slate-900">{formatCurrency(calculateShotValue(shot, version.rates, version.setup), version.setup.currency)}</div>
                              </div>
                            ))}
                            {(version.assets || []).slice(0, 4).map((asset) => (
                              <div key={asset.id} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2">
                                <div className="min-w-0">
                                  <div className="text-[13px] font-medium text-slate-900">{asset.assetCode}</div>
                                  <div className="truncate text-xs text-slate-500">{asset.status || "Bidding"} • {asset.type || "Asset"}</div>
                                </div>
                                <div className="text-[13px] font-semibold text-slate-900">{formatCurrency(calculateAssetValue(asset, version.assetRates, version.setup), version.setup.currency)}</div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="rounded-xl border border-slate-200 p-4">
                          <div className="mb-3 flex items-center gap-2">
                            <Receipt className="h-4 w-4 text-slate-400" />
                            <h4 className="text-sm font-semibold text-slate-900">Purchase Orders</h4>
                          </div>
                          <div className="space-y-2">
                            {linkedPOs.length > 0 ? (
                              linkedPOs.map((po) => (
                                <div key={po.id} className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[13px] font-semibold text-slate-900">{po.poNumber}</span>
                                    <span className="text-slate-500">{po.status}</span>
                                  </div>
                                  <div className="mt-1 text-xs text-slate-500">
                                    {formatCurrency(po.amount, version.setup.currency)} issued {new Date(po.issueDate).toLocaleDateString()}
                                  </div>
                                  <div className="mt-1 text-xs text-slate-500">
                                    Remaining {formatCurrency(po.remainingAmount, version.setup.currency)}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-sm text-slate-500">No purchase orders linked to this version yet.</div>
                            )}

                            {showPOFormForVersion === version.id && (
                              <div className="mt-4 space-y-2 rounded-xl border border-slate-200 p-3">
                                <input
                                  value={poDraft.poNumber}
                                  onChange={(event) => setPoDraft((prev) => ({ ...prev, poNumber: event.target.value }))}
                                  placeholder="PO Number"
                                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                />
                                <div className="grid grid-cols-2 gap-2">
                                  <input
                                    value={poDraft.issueDate}
                                    type="date"
                                    onChange={(event) => setPoDraft((prev) => ({ ...prev, issueDate: event.target.value }))}
                                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                  />
                                  <input
                                    value={poDraft.amount}
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    onChange={(event) => setPoDraft((prev) => ({ ...prev, amount: event.target.value }))}
                                    placeholder="Amount"
                                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                  />
                                </div>
                                <input
                                  value={poDraft.linkedEpisode}
                                  onChange={(event) => setPoDraft((prev) => ({ ...prev, linkedEpisode: event.target.value }))}
                                  placeholder="Linked episode (optional)"
                                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                                />
                                <button
                                  onClick={() => handleSubmitPO(version.id)}
                                  className="rounded-lg bg-[#ffcc01] px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-[#e6b801]"
                                >
                                  Add PO
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-3 flex items-center gap-2">
              <Wallet className="h-4 w-4 text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Operations Snapshot</h3>
            </div>
            <div className="space-y-2 text-sm text-slate-600">
              <div className="flex items-center justify-between">
                <span>Purchase orders on file</span>
                <span className="font-semibold text-slate-900">{purchaseOrders.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Awarded work not yet covered by PO</span>
                <span className="font-semibold text-slate-900">{formatCurrency(unlinkedAwardedValue, setup.currency)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
